if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Frame add-on: re-price the picker's tiles whenever the customer switches variant.
        // TBPFrame may not exist at all (untagged product, or frames disabled) -- every access
        // below is guarded. See getSelectedVariantSize() for how the size is derived.
        this.variantIdInput = this.form.querySelector('[name=id]');
        if (this.variantIdInput) {
          this.variantIdInput.addEventListener('change', this.onVariantIdChanged.bind(this));
        }

        // Seed the picker with the ALREADY-selected variant's size/price at boot -- without
        // this, every tile reads as unresolved (now correctly disabled, see frame-addon.js's
        // resolveForSize) until the customer touches the variant selector, which on a page load
        // reads as "frames are sold out". frame-addon.js and this script are both `defer`, and
        // their relative execution order between each other isn't guaranteed, so don't assume
        // window.TBPFrame exists yet: try now, and try again once on window 'load' (by which
        // point every deferred script has definitely run). onVariantIdChanged only calls plain
        // setters (setSize/setPosterPrice), so calling it twice -- or with TBPFrame absent both
        // times, e.g. an untagged product -- is harmless.
        this.onVariantIdChanged();
        window.addEventListener('load', () => this.onVariantIdChanged(), { once: true });
      }

      onVariantIdChanged() {
        if (!window.TBPFrame) return;
        const variant = this.getSelectedVariant();
        if (typeof window.TBPFrame.setSize === 'function') {
          window.TBPFrame.setSize(this.getSizeFromVariant(variant));
        }
        // Feeds the frame picker's "poster + frame = total" footer note (frame-addon.js's
        // renderNote) so it stays right when the customer switches variant without having to
        // re-select the frame. variant.price is already cents (same shape as posterPrice
        // emitted by snippets/frame-picker.liquid); setPosterPrice itself falls back to
        // "unknown" for anything else, so passing undefined here on a lookup miss is safe.
        if (typeof window.TBPFrame.setPosterPrice === 'function') {
          window.TBPFrame.setPosterPrice(variant && variant.price);
        }
      }

      // Dawn carries size as a variant option, not a separate control, so there's no single
      // "size input" to read; getSelectedVariant() below looks up the variant matching the id
      // input's new value in the variant-selects component sharing this product-form's section
      // id (it embeds product.variants as JSON -- see snippets/product-variant-picker.liquid).
      // Shared by onVariantIdChanged for both the size (getSizeFromVariant) and the poster price
      // it feeds straight to TBPFrame.setPosterPrice, so the JSON blob is only parsed once.
      getSelectedVariant() {
        const variantId = this.variantIdInput && this.variantIdInput.value;
        const sectionId = this.dataset.sectionId;
        if (!variantId || !sectionId) return null;

        const variantSelects = document.querySelector(`variant-selects[data-section="${sectionId}"]`);
        const variantScript = variantSelects && variantSelects.querySelector('script[type="application/json"]');
        if (!variantScript) return null;

        let variants;
        try {
          variants = JSON.parse(variantScript.textContent);
        } catch (e) {
          return null;
        }
        if (!Array.isArray(variants)) return null;

        return variants.find((v) => String(v.id) === String(variantId)) || null;
      }

      // Pulls a size-like token ("30x40", "50×70", ...) out of a variant's options/title.
      // Returned AS-IS (display form) -- this is what ends up in _frame_size and the visible
      // "for poster" property, both read by a human packing the order, so it must match the
      // storefront's own spelling rather than a normalised token. frame-addon.js normalises
      // internally when matching (setSize stores the raw value; the lookup applies its own
      // normSize), so passing the display form here doesn't break matching. A variant with no
      // recognisable size gets "" back -- setSize("") simply won't match any frame variant and
      // the picker falls back to "no frame", which is fine.
      getSizeFromVariant(variant) {
        if (!variant) return '';

        const sizePattern = /\d+\s*[×x]\s*\d+/i;
        const candidates = [variant.option1, variant.option2, variant.option3, variant.title];
        for (const candidate of candidates) {
          const match = candidate && candidate.match(sizePattern);
          if (match) return match[0];
        }
        return '';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        let sectionsToRender = null;
        if (this.cart) {
          sectionsToRender = this.cart.getSectionsToRender().map((section) => section.id);
          formData.append('sections', sectionsToRender);
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        // Frame add-on: if a frame is currently selected, stamp the shared bundle properties
        // onto the poster's own FormData (still one request for the poster -- unchanged wire
        // shape otherwise) and stash the matching frame line item to add in a second request
        // once the poster is confirmed in the cart, below. TBPFrame may not exist at all on an
        // untagged product; with no frame selected, buildItems() hands the poster back
        // unchanged -- either way formData/frameItem stay untouched and this request is
        // byte-identical to the pre-frame-picker code path.
        let frameItem = null;
        if (window.TBPFrame && typeof window.TBPFrame.getSelection === 'function' && window.TBPFrame.getSelection()) {
          const posterProperties = {};
          for (const [key, value] of formData.entries()) {
            const match = key.match(/^properties\[(.+)\]$/);
            if (match) posterProperties[match[1]] = value;
          }
          const posterStub = {
            id: formData.get('id'),
            quantity: Number(formData.get('quantity')) || 1,
            properties: posterProperties,
            title: this.dataset.productTitle || '',
          };
          const items = window.TBPFrame.buildItems(posterStub);
          if (items.length > 1) {
            const bundleProperties = items[0].properties || {};
            formData.append('properties[_bundle_id]', bundleProperties._bundle_id);
            formData.append('properties[_bundle_role]', bundleProperties._bundle_role || 'poster');
            frameItem = items[1];
          }
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              if (frameItem) {
                // Wait for the frame request before navigating away -- otherwise the page
                // unload cancels it mid-flight and the frame is silently lost.
                this.addFrameLineItem(frameItem, sectionsToRender).finally(() => {
                  window.location = window.routes.cart_url;
                });
              } else {
                window.location = window.routes.cart_url;
              }
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });

            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            const renderCart = (cartResponse) => {
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      this.cart.renderContents(cartResponse);
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                this.cart.renderContents(cartResponse);
              }
            };

            if (frameItem) {
              // Render the drawer/notification once from whichever response ends up being the
              // final cart state, instead of once from the poster response and again (or not
              // at all) from the frame's -- exactly the one renderContents() call per
              // interaction Dawn already makes. On frame failure, fall back to the poster
              // response already in hand so the customer never ends up with an unrendered
              // cart; addFrameLineItem() shows the non-blocking notice in that case.
              this.addFrameLineItem(frameItem, sectionsToRender).then((frameResponse) => {
                renderCart(frameResponse || response);
              });
            } else {
              renderCart(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      // Adds the frame's cart line in its own request, once the poster is already confirmed in
      // the cart (see onSubmitHandler). `sections` (when given) mirrors the poster request's
      // own sections/sections_url so the frame's response can be rendered from directly,
      // showing both lines in one pass. Never rolls the poster back on failure -- it's already
      // safely in the cart -- this only shows a recoverable, non-blocking notice via the same
      // error-message element product-form already uses for real errors. Resolves with the
      // frame's own parsed /cart/add.js response, or null on failure so the caller can fall
      // back to rendering the poster's response instead.
      addFrameLineItem(frameItem, sections) {
        const body = Object.assign({}, frameItem);
        if (sections) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }
        return fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })
          .then((response) => {
            if (!response.ok) throw new Error('frame add failed');
            return response.json();
          })
          .catch((e) => {
            console.error('[product-form] frame add-on failed to add:', e);
            this.handleErrorMessage(
              this.dataset.frameAddFailedMessage || "We couldn't add your frame, but your poster is in your cart."
            );
            return null;
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
